/**
 * PeticionAudioProgress - Barra de progreso + CUE points para peticiones MP3.
 *
 * Fase 1: barra clickable con tiempo mm:ss / mm:ss y seek.
 * Fase 2: hasta 4 CUE points por petición (long-press en barra para guardar,
 * tap en chip para saltar). Persistencia vía PATCH /api/peticiones/{id}/cues.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { haptic } from '../utils/haptic';
import { setCueIntent } from '../utils/cueIntents';

const fmt = (sec) => {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const CUE_COLORS = ['ef4444', 'f59e0b', '10b981', '3b82f6']; // red, amber, emerald, blue

const PeticionAudioProgress = ({ peticion, playlistId, isPlaying, onUpdated, setPlayingAudioId }) => {
  const audioFileId = peticion?.audio_file_id;
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(peticion?.duration || 0);
  const [cues, setCues] = useState(peticion?.cues || []);
  const [savingCue, setSavingCue] = useState(false);
  const [flashedCueId, setFlashedCueId] = useState(null);
  const [pendingCueId, setPendingCueId] = useState(null); // chip mostrado como cargando
  const pendingClearRef = useRef(null);
  const longPressRef = useRef(null);
  const cueLongPressTimerRef = useRef(null);
  const cueLongPressFiredRef = useRef(false);
  const barRef = useRef(null);

  const startCueLongPress = (cueId) => {
    cueLongPressFiredRef.current = false;
    cueLongPressTimerRef.current = setTimeout(() => {
      cueLongPressFiredRef.current = true;
      removeCue(cueId);
      toast.success('CUE eliminado');
    }, 700);
  };

  const cancelCueLongPress = () => {
    if (cueLongPressTimerRef.current) {
      clearTimeout(cueLongPressTimerRef.current);
      cueLongPressTimerRef.current = null;
    }
  };

  // Sync cues con la petición si cambia desde el padre
  useEffect(() => {
    setCues(peticion?.cues || []);
  }, [peticion]);

  // Engancha al elemento <audio> del DOM creado por App.js cuando se reproduce
  useEffect(() => {
    if (!isPlaying || !audioFileId) {
      setTime(0);
      return undefined;
    }
    let cancelled = false;
    let audio = null;

    const attach = () => {
      audio = document.getElementById(`audio-${audioFileId}`);
      if (!audio) return false;
      const onTime = () => !cancelled && setTime(audio.currentTime || 0);
      const onMeta = () => !cancelled && setDuration(audio.duration || 0);
      audio.addEventListener('timeupdate', onTime);
      audio.addEventListener('loadedmetadata', onMeta);
      audio.addEventListener('durationchange', onMeta);
      // Sincroniza estado inmediato
      if (audio.duration && !isNaN(audio.duration)) setDuration(audio.duration);
      if (audio.currentTime) setTime(audio.currentTime);
      audio._progressCleanup = () => {
        audio.removeEventListener('timeupdate', onTime);
        audio.removeEventListener('loadedmetadata', onMeta);
        audio.removeEventListener('durationchange', onMeta);
      };
      return true;
    };

    if (!attach()) {
      // El audio puede no estar montado aún; reintentamos brevemente
      const id = setInterval(() => attach() && clearInterval(id), 200);
      setTimeout(() => clearInterval(id), 5000);
      return () => clearInterval(id);
    }

    return () => {
      cancelled = true;
      if (audio && audio._progressCleanup) audio._progressCleanup();
    };
  }, [isPlaying, audioFileId]);

  const seek = useCallback(
    (sec) => {
      const audio = document.getElementById(`audio-${audioFileId}`);
      if (!audio) return;
      audio.currentTime = Math.max(0, Math.min(sec, audio.duration || sec));
    },
    [audioFileId]
  );

  /** Salta a un punto y reproduce. Usado por los chips de CUE.
   *  Si el <audio> no está montado, dispara la carga automáticamente simulando
   *  un click en el botón Play de la petición (registra la intención de seek
   *  en `cueIntents` para que arranque directamente en la posición del CUE).
   *  Dispara haptic + flash visual del CUE tocado para confirmar acción. */
  const seekAndPlay = useCallback(
    (sec, cueId = null) => {
      haptic(15);
      if (cueId) {
        setFlashedCueId(cueId);
        setTimeout(() => setFlashedCueId(null), 220);
      }
      const audio = document.getElementById(`audio-${audioFileId}`);
      if (!audio) {
        // Audio no montado — cargar automáticamente disparando el botón Play
        // con la intención de saltar al CUE nada más quede listo.
        const playBtn = document.querySelector(
          `[data-testid="play-audio-button-${peticion.id || audioFileId}"]`
        );
        if (!playBtn) {
          toast.info('Pulsa Play primero para activar la pista');
          return;
        }
        if (playBtn.disabled) {
          toast.info('Espera a que termine de cargar la pista…');
          return;
        }
        setCueIntent(audioFileId, sec);
        if (cueId) {
          setPendingCueId(cueId);
          // Safety timeout: si algo falla en la carga, limpiamos el spinner
          if (pendingClearRef.current) clearTimeout(pendingClearRef.current);
          pendingClearRef.current = setTimeout(() => setPendingCueId(null), 45000);
        }
        toast.loading('Cargando pista y saltando al CUE…', { id: `cue-load-${peticion.id}`, duration: 8000 });
        // Simular click de usuario (mismo gesto → iOS acepta autoplay)
        try {
          playBtn.click();
        } catch (err) {
          console.error('[CUE] Error disparando Play:', err);
          setPendingCueId(null);
          toast.error('No se pudo activar la pista', { id: `cue-load-${peticion.id}` });
        }
        return;
      }
      try {
        audio.currentTime = Math.max(0, Math.min(sec, audio.duration || sec));
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch((err) => {
            // Safari iOS exige interacción del usuario; el tap del chip cuenta como tal.
            console.warn('[CUE] play() rechazado:', err?.message);
          });
        }
        if (typeof setPlayingAudioId === 'function') setPlayingAudioId(audioFileId);
      } catch (err) {
        console.warn('[CUE] error reproduciendo:', err);
      }
    },
    [audioFileId, peticion.id, setPlayingAudioId]
  );

  // Limpia el spinner de CUE en cuanto la pista arranca (o si el usuario detiene)
  useEffect(() => {
    if (pendingCueId && isPlaying) {
      setPendingCueId(null);
      if (pendingClearRef.current) {
        clearTimeout(pendingClearRef.current);
        pendingClearRef.current = null;
      }
      toast.success('Reproduciendo desde el CUE', { id: `cue-load-${peticion.id}`, duration: 1500 });
    }
  }, [isPlaying, pendingCueId, peticion.id]);

  // Cleanup del timeout al desmontar
  useEffect(() => {
    return () => {
      if (pendingClearRef.current) clearTimeout(pendingClearRef.current);
    };
  }, []);

  const handleBarClick = (e) => {
    const bar = barRef.current;
    if (!bar || !duration) return;
    const rect = bar.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || 0) - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    seek(ratio * duration);
  };

  const persistCues = async (newCues) => {
    setSavingCue(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${BACKEND_URL}/api/peticiones/${peticion.id}/cues`,
        { cues: newCues },
        { params: { playlist_id: playlistId }, headers: { Authorization: `Bearer ${token}` } }
      );
      setCues(newCues);
      if (typeof onUpdated === 'function') onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando CUE');
    } finally {
      setSavingCue(false);
    }
  };

  const addCueAtCurrent = () => {
    if (!duration) return;
    if (cues.length >= 4) {
      toast.warning('Máximo 4 CUEs por petición');
      return;
    }
    const newCue = {
      id: `cue-${Date.now()}`,
      label: String.fromCharCode(65 + cues.length), // A, B, C, D
      time: Number((time || 0).toFixed(2)),
      color: CUE_COLORS[cues.length] || CUE_COLORS[0],
    };
    persistCues([...cues, newCue]);
    toast.success(`CUE ${newCue.label} guardado @ ${fmt(newCue.time)}`);
  };

  const removeCue = (cueId) => {
    persistCues(cues.filter((c) => c.id !== cueId));
  };

  const startLongPress = (e) => {
    longPressRef.current = setTimeout(() => {
      // Long-press: guarda CUE en la posición tocada
      const bar = barRef.current;
      if (!bar || !duration) return;
      const rect = bar.getBoundingClientRect();
      const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || 0) - rect.left;
      const ratio = Math.max(0, Math.min(1, x / rect.width));
      const cueTime = ratio * duration;
      if (cues.length >= 4) {
        toast.warning('Máximo 4 CUEs');
        return;
      }
      const newCue = {
        id: `cue-${Date.now()}`,
        label: String.fromCharCode(65 + cues.length),
        time: Number(cueTime.toFixed(2)),
        color: CUE_COLORS[cues.length] || CUE_COLORS[0],
      };
      persistCues([...cues, newCue]);
      toast.success(`CUE ${newCue.label} @ ${fmt(cueTime)}`);
    }, 700); // 700ms = long-press
  };

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  if (!audioFileId) return null;

  const pct = duration > 0 ? (time / duration) * 100 : 0;

  return (
    <div className="mt-2 select-none" data-testid={`audio-progress-${peticion.id}`}>
      {/* Wrapper con hit-area vertical extendida para seek preciso en móvil */}
      <div
        className="relative py-3 -my-2 cursor-pointer touch-none"
        onClick={handleBarClick}
        onMouseDown={startLongPress}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
        title="Click: saltar · Long-press: guardar CUE aquí"
      >
        {/* Barra visible — h-5 (20px) para mejor precisión de tap */}
        <div
          ref={barRef}
          className="relative w-full h-5 bg-slate-700 rounded-full"
        >
          <div
            className="absolute top-0 left-0 h-full bg-pink-500 rounded-full"
            style={{ width: `${pct}%` }}
          />
          {/* Marcadores de CUE — 16×28px para visibilidad y hit-area en Live */}
          {duration > 0 &&
            cues.map((c) => {
              const left = Math.min(100, (c.time / duration) * 100);
              const isFlashed = flashedCueId === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    seekAndPlay(c.time, c.id);
                  }}
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-7 rounded-sm border border-white/40 transition-transform ${isFlashed ? 'scale-150' : 'hover:scale-110'}`}
                  style={{ left: `${left}%`, backgroundColor: `#${c.color || 'ef4444'}` }}
                  title={`CUE ${c.label} @ ${fmt(c.time)}`}
                  data-testid={`cue-marker-${c.id}`}
                />
              );
            })}
        </div>
      </div>

      {/* Tiempos + chips de CUE */}
      <div className="flex items-center justify-between mt-1.5 gap-2 flex-wrap">
        <span className="text-xs text-slate-400 tabular-nums">
          {fmt(time)} / {fmt(duration)}
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {cues.map((c) => {
            const isFlashed = flashedCueId === c.id;
            const isPending = pendingCueId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  if (cueLongPressFiredRef.current) {
                    cueLongPressFiredRef.current = false;
                    return;
                  }
                  seekAndPlay(c.time, c.id);
                }}
                onMouseDown={() => startCueLongPress(c.id)}
                onMouseUp={cancelCueLongPress}
                onMouseLeave={cancelCueLongPress}
                onTouchStart={() => startCueLongPress(c.id)}
                onTouchEnd={cancelCueLongPress}
                onTouchCancel={cancelCueLongPress}
                className={`px-2 py-2 -my-1.5 rounded text-[11px] font-bold text-white select-none touch-none transition-transform ${isFlashed ? 'scale-125 ring-2 ring-white/60' : ''} ${isPending ? 'opacity-70 animate-pulse' : ''}`}
                style={{ backgroundColor: `#${c.color || 'ef4444'}` }}
                data-testid={`cue-chip-${c.id}`}
                title={`Tap: reproducir desde ${fmt(c.time)} · Long-press: eliminar`}
                disabled={isPending}
              >
                {isPending ? `${c.label} · …` : `${c.label} · ${fmt(c.time)}`}
              </button>
            );
          })}
          {cues.length < 4 && (
            <button
              type="button"
              onClick={() => { haptic(10); addCueAtCurrent(); }}
              disabled={savingCue || !duration}
              className="inline-flex items-center gap-0.5 px-2 py-2 -my-1.5 rounded text-[11px] bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-40"
              title="Guardar CUE en la posición actual"
              data-testid={`cue-add-${peticion.id}`}
            >
              <Plus className="w-3 h-3" /> CUE
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeticionAudioProgress;
